import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { X, Trash2, Plus, Minus, ArrowUpRight } from "lucide-react";
import { useCart } from "../context/CartContext";

export default function CartDrawer() {
  const { open, setOpen, items, remove, updateQty, total, clear } = useCart();

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[70] bg-black/40 transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setOpen(false)}
      />
      {/* Drawer */}
      <aside
        className={`fixed top-0 right-0 z-[80] h-full w-full sm:w-[420px] bg-[var(--lc-cream)] shadow-2xl transition-transform duration-300 flex flex-col ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-[var(--lc-line)]">
          <div>
            <div className="eyebrow text-[var(--lc-muted)]">Your Selection</div>
            <div className="font-serif-display text-xl mt-1">Custom Package</div>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="p-2 -mr-2 hover:opacity-70"
            aria-label="Close cart"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-20">
              <div className="w-16 h-16 rounded-full bg-[var(--lc-cream-soft)] grid place-items-center mb-4">
                <ArrowUpRight className="h-6 w-6 text-[var(--lc-muted)]" />
              </div>
              <h3 className="font-serif-display text-2xl mb-2">Empty for now</h3>
              <p className="text-sm text-[var(--lc-muted)] mb-6 max-w-xs">
                Build your own plan by adding services across our six pillars.
              </p>
              <Link
                to="/build"
                onClick={() => setOpen(false)}
                className="px-5 py-2.5 bg-[var(--lc-ink)] text-[var(--lc-cream)] text-[12px] font-bold tracking-[0.18em] uppercase hover:bg-[var(--lc-moss-dark)] transition-colors"
              >
                Browse Services
              </Link>
            </div>
          ) : (
            <ul className="divide-y divide-[var(--lc-line)]">
              {items.map((it) => (
                <li key={it.id} className="py-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--lc-moss)] font-semibold">
                        {it.pillarName}
                      </div>
                      <div className="font-serif-display text-lg leading-tight mt-1">
                        {it.name}
                      </div>
                      <div className="text-xs text-[var(--lc-muted)] mt-1">
                        per {it.unit}
                      </div>
                    </div>
                    <button
                      onClick={() => remove(it.id)}
                      className="p-1.5 text-[var(--lc-muted)] hover:text-[var(--lc-ink)]"
                      aria-label="Remove"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <div className="inline-flex items-center border border-[var(--lc-line)] rounded-full">
                      <button
                        onClick={() => updateQty(it.id, it.qty - 1)}
                        className="w-8 h-8 grid place-items-center hover:bg-[var(--lc-cream-soft)] rounded-l-full"
                        aria-label="Decrease"
                      >
                        <Minus className="h-3 w-3" />
                      </button>
                      <span className="min-w-[28px] text-center text-sm font-semibold">{it.qty}</span>
                      <button
                        onClick={() => updateQty(it.id, it.qty + 1)}
                        className="w-8 h-8 grid place-items-center hover:bg-[var(--lc-cream-soft)] rounded-r-full"
                        aria-label="Increase"
                      >
                        <Plus className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-[var(--lc-line)] px-6 py-5 space-y-4">
            <div>
              <div className="eyebrow text-[var(--lc-muted)]">Investment</div>
              <div className="font-serif-display text-2xl leading-tight mt-1">Reach out to <span className="italic">discuss.</span></div>
              <div className="text-xs text-[var(--lc-muted)] mt-1">Tailored quote sent within 24 hours</div>
            </div>
            <Link
              to="/checkout"
              onClick={() => setOpen(false)}
              className="flex items-center justify-center gap-2 w-full px-6 py-4 bg-[var(--lc-ink)] text-[var(--lc-cream)] text-[12px] font-bold tracking-[0.18em] uppercase hover:bg-[var(--lc-moss-dark)] transition-colors"
            >
              Request Quote <ArrowUpRight className="h-4 w-4" />
            </Link>
            <button
              onClick={clear}
              className="w-full text-xs tracking-[0.16em] uppercase text-[var(--lc-muted)] hover:text-[var(--lc-ink)]"
            >
              Clear all
            </button>
          </div>
        )}
      </aside>
    </>
  );
}
